/**
 * Language Context for Bharat Darshan Website
 *
 * - Stores user's selected language in localStorage
 * - Provides `language`, `changeLanguage`, `t()` (static keys), `useTranslated()` (dynamic text via API)
 * - 8 supported languages: en, hi, mr, pa, gu, bn, te, or
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'bd_language';
const DEFAULT_LANG = 'en';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', native: 'English', flag: '🇬🇧' },
  { code: 'hi', name: 'Hindi', native: 'हिंदी', flag: '🇮🇳' },
  { code: 'mr', name: 'Marathi', native: 'मराठी', flag: '🇮🇳' },
  { code: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ', flag: '🇮🇳' },
  { code: 'gu', name: 'Gujarati', native: 'ગુજરાતી', flag: '🇮🇳' },
  { code: 'bn', name: 'Bengali', native: 'বাংলা', flag: '🇮🇳' },
  { code: 'te', name: 'Telugu', native: 'తెలుగు', flag: '🇮🇳' },
  { code: 'or', name: 'Odia', native: 'ଓଡ଼ିଆ', flag: '🇮🇳' },
];

const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

// Lazy-loaded translation files
const TRANSLATION_LOADERS = {
  en: () => import('../utils/i18n/en'),
  hi: () => import('../utils/i18n/hi'),
  mr: () => import('../utils/i18n/mr'),
  pa: () => import('../utils/i18n/pa'),
  gu: () => import('../utils/i18n/gu'),
  bn: () => import('../utils/i18n/bn'),
  te: () => import('../utils/i18n/te'),
  or: () => import('../utils/i18n/or'),
};

const LanguageContext = createContext({
  language: DEFAULT_LANG,
  changeLanguage: () => {},
  t: (key, fallback) => fallback || key,
});

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    try {
      const saved = typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY);
      return saved && SUPPORTED_CODES.includes(saved) ? saved : DEFAULT_LANG;
    } catch {
      return DEFAULT_LANG;
    }
  });
  const [dict, setDict] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loader = TRANSLATION_LOADERS[language] || TRANSLATION_LOADERS.en;
        const mod = await loader();
        if (!cancelled) setDict((mod && (mod.default || mod)) || {});
      } catch (e) {
        if (!cancelled) setDict({});
      }
    })();
    return () => { cancelled = true; };
  }, [language]);

  const changeLanguage = useCallback((code) => {
    if (!SUPPORTED_CODES.includes(code)) return;
    setLanguage(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch {}
  }, []);

  // Static key-based translation, dot-notation: t('common.home', 'Home')
  const t = useCallback((key, fallback) => {
    if (!key) return fallback || '';
    const parts = key.split('.');
    let cur = dict;
    for (const p of parts) {
      if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
      else return fallback || key;
    }
    return (typeof cur === 'string') ? cur : (fallback || key);
  }, [dict]);

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
export default LanguageContext;
